// @ts-nocheck
import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { useParams, Link } from 'react-router-dom';
import useDivisionStore from '@store/divisionStore';
import useTeamDivisionStore from '@store/teamDivisionStore';
import helpers from '@/utils/helpers';
import { getDivisionCapacityCounts } from '@staty/shared/utils/waitlist';
import { useAuth } from '@/auth/clerkShim';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card"
import { Button } from '@components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuRadioGroup, DropdownMenuRadioItem } from "@/components/ui/dropdown-menu"
import { ChevronDown, GripVertical, Info } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { PageContainer, PageHeader } from '@/components/Page/PageContainer';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
} from "@dnd-kit/core"
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"

export default function DivisionSchedule() {
  const { getToken } = useAuth();
  const { leagueId, contestType } = useParams();
  const [acceptanceFilter, setAcceptanceFilter] = useState('all');
  const [activeId, setActiveId] = useState<string | null>(null);

  const { divisions, addDivision, deleteDivision, updateDivision, getDivisionsByLeagueId } = useDivisionStore((state: any) => ({
    divisions: _.filter(state.divisions, (division) => helpers.getObjId(division.league) === leagueId),
    addDivision: state.addDivision(getToken),
    deleteDivision: state.deleteDivision,
    updateDivision: state.updateDivision,
    getDivisionsByLeagueId: state.getDivisionsByLeagueId,
  }));

  const { teamDivisions, updateTeamDivision, getTeamDivisionsBySeasonId } = useTeamDivisionStore((state: any) => ({
    teamDivisions: state.teamDivisions,
    getTeamDivisionsBySeasonId: state.getTeamDivisionsBySeasonId,
    updateTeamDivision: state.updateTeamDivision(getToken),
  }));

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    getDivisionsByLeagueId(leagueId);
    getTeamDivisionsBySeasonId(leagueId);
    console.log('getDivisionsByLeagueId');
  }, []);

  // Function to get teams for a division with proper seeding
  const getTeamsForDivision = (divisionId: string) => {
    const teams = _.filter(teamDivisions, (teamDivision) => 
      helpers.getObjId(teamDivision.division) === helpers.getObjId(divisionId)
    );
    
    // Sort teams by seed, with unseeded teams at the bottom
    return _.orderBy(teams, [
      (team) => team.seed === undefined || team.seed === null ? 999999 : team.seed
    ], ['asc']);
  };

  // Function to update seeds for all teams in a division
  const updateSeedsForDivision = async (divisionId: string) => {
    const teams = getTeamsForDivision(divisionId);
    const updates = teams.map((team, index) => ({
      ...team,
      seed: index + 1
    }));

    // Update all teams with new seeds
    for (const team of updates) {
      await updateTeamDivision(team);
    }
  };

  // Move a team into a different division by sending only the fields the
  // backend mutates. Spreading the populated teamDivision would send nested
  // ref objects and trigger a Mongoose CastError.
  const addTeamToDivision = async (teamDivision: any, divisionId: string) => {
    const targetDivisionId = helpers.getObjId(divisionId);
    const currentTeamsInTarget = getTeamsForDivision(targetDivisionId);

    const payload = {
      _id: teamDivision._id,
      division: targetDivisionId,
      seed: currentTeamsInTarget.length + 1,
    };

    const registrationTeamId = helpers.getObjId(teamDivision.registrationTeam);
    await updateTeamDivision(payload, registrationTeamId);
  };

  // Function to handle adding a new team to a division
  const handleAddTeamToDivision = async (teamDivision: any, divisionId: string) => {
    try {
      await addTeamToDivision(teamDivision, divisionId);
    } catch (error) {
      console.error('Error adding team to division:', error);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) {
      return;
    }

    const activeTeam = teamDivisions.find(team => team._id === active.id);
    if (!activeTeam) {
      return;
    }
    const activeDivisionId = helpers.getObjId(activeTeam.division);

    // Drop landed directly on a division card (including empty placeholder)
    const overDivision = sortedDivisions.find(d => d._id === over.id);
    if (overDivision && overDivision._id !== activeDivisionId) {
      await handleAddTeamToDivision(activeTeam, overDivision._id);
      return;
    }

    // Otherwise the drop should be on another team
    const overTeam = teamDivisions.find(team => team._id === over.id);
    if (!overTeam) {
      return;
    }
    const overTeamDivisionId = helpers.getObjId(overTeam.division);

    // Cross-division drop onto a team in a different division
    if (activeDivisionId !== overTeamDivisionId) {
      await handleAddTeamToDivision(activeTeam, overTeamDivisionId);
      return;
    }

    // Same-division reorder
    const divisionId = activeTeam.division;
    const teams = getTeamsForDivision(divisionId);
    const oldIndex = teams.findIndex(team => team._id === active.id);
    const newIndex = teams.findIndex(team => team._id === over.id);

    if (oldIndex !== newIndex) {
      const reorderedTeams = arrayMove(teams, oldIndex, newIndex);
      
      // Update seeds for all teams in the new order
      const updates = reorderedTeams.map((team, index) => ({
        ...team,
        seed: index + 1
      }));

      // Update all teams with new seeds
      for (const team of updates) {
        await updateTeamDivision(team);
      }
    }
  };

  const handleDragOver = (event: DragEndEvent) => {
    // This function can be used to provide visual feedback during drag
    // For now, we'll keep it simple
  };

  const getAcceptanceFilterLabel = () => {
    switch (acceptanceFilter) {
      case "accepted":
        return "Accepted"
      case "not-accepted":
        return "Not Accepted"
      default:
        return "All"
    }
  }

  const sortedDivisions = _.orderBy(divisions, ['priority'], ['desc']);
  const filteredTeams = _.filter(teamDivisions, (team) => {
    if (acceptanceFilter === 'accepted') {
      return _.get(team, 'registrationTeam.status') === 'accepted';
    } else if (acceptanceFilter === 'not-accepted') {
      return _.get(team, 'registrationTeam.status') !== 'accepted';
    } else {
      return true;
    }
  });

  console.log('divisions: ', divisions);
  console.log('teamDivisions: ', teamDivisions);

  return (
    <PageContainer fullWidth>
      <div className="w-full">
        <div className="w-full">
          {/* Safe to wrap in PageContainer since `.page-enter` switched to
              fill-mode `backwards` — the transform clears after the mount
              animation, so the inline fixed DragOverlay anchors to the
              viewport again by the time a drag can start. */}
          <PageHeader
            className="my-4"
            title="Division Breakdown"
            description={
              <span className="inline-flex items-center gap-2">
                Drag and drop teams to desired division or reorder within division
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="h-4 w-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <div className="max-w-xs">
                        <p className="font-medium mb-2">Seeding System:</p>
                        <ul className="text-sm space-y-1">
                          <li>• Teams are automatically seeded by their position in the list</li>
                          <li>• Drag teams to reorder and update seeds</li>
                          <li>• Teams moved to a new division are added at the bottom</li>
                          <li>• Unseeded teams appear at the bottom with "NS" badge</li>
                        </ul>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </span>
            }
            actions={
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="justify-between">
                      {getAcceptanceFilterLabel()}
                      <ChevronDown className="ml-2 h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-full">
                    <DropdownMenuRadioGroup value={acceptanceFilter} onValueChange={setAcceptanceFilter}>
                      <DropdownMenuRadioItem value="all">All</DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="accepted">Accepted</DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="not-accepted">Not Accepted</DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button onClick={() => addDivision({ name: 'New Division', season: leagueId })}>Add Division</Button>
              </>
            }
          />
          
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
          >
            {(() => {
              const activeTeam = activeId ? teamDivisions.find(t => t._id === activeId) : null;
              const activeSourceDivisionId = activeTeam ? helpers.getObjId(activeTeam.division) : null;
              const isDragActive = !!activeId;
              return (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {sortedDivisions.map((division: any) => {
                    const teams = getTeamsForDivision(division._id);
                    const filteredTeamsInDivision = teams.filter(team => {
                      if (acceptanceFilter === 'accepted') {
                        return _.get(team, 'registrationTeam.status') === 'accepted';
                      } else if (acceptanceFilter === 'not-accepted') {
                        return _.get(team, 'registrationTeam.status') !== 'accepted';
                      } else {
                        return true;
                      }
                    });

                    return (
                      <DroppableDivisionCard
                        key={division._id}
                        division={division}
                        teams={teams}
                        filteredTeamsInDivision={filteredTeamsInDivision}
                        isDragActive={isDragActive}
                        isSource={activeSourceDivisionId === division._id}
                      />
                    );
                  })}
                </div>
              );
            })()}
            
            <DragOverlay>
              {activeId ? (
                <div className="p-2 bg-muted rounded-md cursor-move shadow-lg opacity-80 border-2 border-primary">
                  {teamDivisions.find(team => team._id === activeId)?.name}
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      </div>
    </PageContainer>
  );
};

// Division card that registers itself as a dnd-kit drop target so teams can
// be dragged onto the whole card (including the empty placeholder), not just
// onto existing team items.
function DroppableDivisionCard({
  division,
  teams,
  filteredTeamsInDivision,
  isDragActive,
  isSource,
}: {
  division: any;
  teams: any[];
  filteredTeamsInDivision: any[];
  isDragActive: boolean;
  isSource: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: division._id });
  const cfg = division.config || {};
  const acceptedCount = teams.filter(t => _.get(t, 'registrationTeam.status') === 'accepted').length;
  const registrationTeams = teams.map(t => t.registrationTeam).filter(Boolean);
  const counts = cfg.capacity ? getDivisionCapacityCounts(registrationTeams, division) : null;

  const isDropTarget = isDragActive && !isSource;
  const isHotTarget = isDropTarget && isOver;

  const cardClass = [
    'relative transition-all duration-150',
    isSource && isDragActive ? 'opacity-60' : '',
  ].join(' ').trim();

  const dropZoneClass = [
    'flex flex-col gap-2 rounded-md min-h-[60px] border-2 p-1 transition-colors',
    isHotTarget
      ? 'border-solid border-primary bg-primary/15 shadow-inner'
      : isDropTarget
      ? 'border-dashed border-primary/40 bg-primary/5'
      : 'border-transparent',
  ].join(' ');

  const emptyPlaceholderClass = [
    'p-4 text-center border-2 border-dashed rounded-md',
    isDropTarget ? 'border-primary text-primary' : 'border-muted text-muted-foreground',
  ].join(' ');

  return (
    <Card className={cardClass}>
      <CardHeader>
        <CardTitle>{division.name}</CardTitle>
        <CardDescription>
          {filteredTeamsInDivision.length} team{filteredTeamsInDivision.length !== 1 ? "s" : ""}
          {cfg.capacity ? (
            <span className="ml-2 text-xs">
              · {acceptedCount} / {cfg.capacity} registered
              {cfg.waitlistCapacity > 0 && counts && ` · ${counts.waitlisted} waitlisted`}
            </span>
          ) : null}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <div ref={setNodeRef} className={dropZoneClass}>
          {filteredTeamsInDivision.length === 0 ? (
            <div className={emptyPlaceholderClass}>
              <p className="text-sm">Drop teams here</p>
            </div>
          ) : (
            <>
              <SortableContext
                items={filteredTeamsInDivision.map(team => team._id)}
                strategy={verticalListSortingStrategy}
              >
                {filteredTeamsInDivision.map((teamDivision, index) => (
                  <SortableTeamItem
                    key={teamDivision._id}
                    teamDivision={teamDivision}
                    index={index}
                    isSeeded={teamDivision.seed !== undefined && teamDivision.seed !== null}
                  />
                ))}
              </SortableContext>
              {isDropTarget && (
                <div className="text-xs text-primary/70 italic text-center py-1">
                  Drop to move here
                </div>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Sortable team item component
function SortableTeamItem({
  teamDivision, 
  index, 
  isSeeded 
}: { 
  teamDivision: any; 
  index: number; 
  isSeeded: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ 
    id: teamDivision._id 
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className={`${isDragging ? "opacity-50" : ""} p-2 bg-muted rounded-md cursor-move hover:bg-muted/80 transition-colors`}
    >
      <div className="flex items-center gap-2">
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted/60 rounded"
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </div>
        
        {isSeeded ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Badge variant="secondary" className="min-w-[32px] h-6 flex items-center justify-center text-xs font-medium">
                  {teamDivision.seed}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <p>Seed {teamDivision.seed} - {teamDivision.seed === 1 ? 'Top seed' : `${teamDivision.seed}${getOrdinalSuffix(teamDivision.seed)} place`}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Badge variant="outline" className="min-w-[32px] h-6 flex items-center justify-center text-xs text-muted-foreground">
                  NS
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <p>Not Seeded - This team will appear at the bottom of the list</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        
        <span className="flex-1 text-sm font-medium">
          {teamDivision.name}
        </span>
      </div>
    </div>
  );
}

// Helper function to get ordinal suffix
function getOrdinalSuffix(num: number): string {
  const j = num % 10;
  const k = num % 100;
  if (j === 1 && k !== 11) {
    return "st";
  }
  if (j === 2 && k !== 12) {
    return "nd";
  }
  if (j === 3 && k !== 13) {
    return "rd";
  }
  return "th";
}

function XIcon(props) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  )
}